import React, { useState, useMemo, useCallback, useEffect } from 'react'
import './style.css'

import { ContractPerformance } from '../../../../Contracts/types'
import HoverTooltip from '../../../../../../components/HoverTooltip'

interface PieChartData {
    label: string
    value: number
    isTotal?: boolean // <-- новое
}

type PerformanceChartProps = {
    contractRegNumber: string
    contractStartDate: string
    contractEndDate: string
    initialIndex: number
    totalIndex?: number
    theme: string
}

const LABEL_COLOR_INDEX: Record<string, number> = {
    'Срок исполнения': 0,
    'Прошло времени': 1,
    'Осталось времени': 2,
    'Просрочка по сроку': 3, // <-- без кавычек - нет пробелов
}

const PieChartPerformanceDate: React.FC<PerformanceChartProps> = ({
    contractRegNumber,
    contractStartDate,
    contractEndDate,
    initialIndex,
    totalIndex,
    theme,
}) => {
    const [pieData, setPieData] = useState<PieChartData[]>([])
    const [selectedLabel, setSelectedLabel] = useState<string>('')
    const [isAnimated, setIsAnimated] = useState<boolean>(false)
    const [isFirstAnimationDone, setIsFirstAnimationDone] =
        useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(true)

    const [performanceCompleted, setPerformanceCompleted] =
        useState<ContractPerformance>()

    const [isCompleted, setIsCompleted] = useState<boolean>(false)
    const [completionStageEndDate, setCompletionStageEndDate] = useState<
        string | null
    >(null)
    const [isPerfLoaded, setIsPerfLoaded] = useState<boolean>(false)

    const [infoTooltip, setInfoTooltip] = useState<{
        x: number
        y: number
    } | null>(null)

    const colors =
        theme == 'dark'
            ? ['#3C18BC', '#649A3B', '#5338ed', '#c15c5c']
            : ['#c7e4fe', '#83d2b7', '#abd7fe', '#ed9cae']

    const loadPerformanceCompleted =
        async (): Promise<ContractPerformance | null> => {
            try {
                const response = await fetch(
                    process.env.REACT_APP_API_URL +
                        `/api/backend/v1/performance_contract_completed/contract_reg_number/${contractRegNumber}`,
                    {
                        method: 'GET',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                    }
                )

                if (!response.ok) {
                    throw new Error('Failed to fetch performance chart')
                }

                const data: ContractPerformance = await response.json()
                setPerformanceCompleted(data)

                const completed = data.completed ?? false
                setIsCompleted(completed)

                const completionDate =
                    (data as any).completion_stage_end_date ?? null
                setCompletionStageEndDate(completionDate)

                return data
            } catch (e) {
                console.error(e)
                setIsCompleted(false)
                setCompletionStageEndDate(null)
                return null
            } finally {
                setIsPerfLoaded(true)
            }
        }

    const MS_PER_DAY = 24 * 60 * 60 * 1000

    const isYmd = (s: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(s)

    // Парсим YYYY-MM-DD как UTC (без влияния локальной таймзоны)
    const parseYmdUtc = (ymd: string): Date => {
        if (!isYmd(ymd))
            throw new Error(
                `Invalid date format, expected YYYY-MM-DD, got: ${ymd}`
            )
        const [y, m, d] = ymd.split('-').map(Number)
        return new Date(Date.UTC(y, m - 1, d))
    }

    // "Сегодня" в формате UTC-полночь (чтобы считать дни корректно)
    const todayUtc = (): Date => {
        const now = new Date()
        return new Date(
            Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
        )
    }

    const isOverdue = todayUtc() > parseYmdUtc(contractEndDate)

    // Разница в днях (end - start).
    // Например: start=2025-01-01, end=2025-01-02 => 1 день
    const diffDays = (start: Date, end: Date): number =>
        Math.floor((end.getTime() - start.getTime()) / MS_PER_DAY)

    useEffect(() => {
        setIsPerfLoaded(false)
        loadPerformanceCompleted()
    }, [contractRegNumber])

    // ---- build pie data based on rules ----
    useEffect(() => {
        if (
            contractStartDate === 'Нет данных' ||
            contractEndDate === 'Нет данных' ||
            !contractStartDate ||
            !contractEndDate
        ) {
            setPieData([])
            setSelectedLabel('')
            setIsLoading(false)
            return
        }

        if (!isYmd(contractStartDate) || !isYmd(contractEndDate)) {
            setPieData([])
            setSelectedLabel('')
            setIsLoading(false)
            return
        }

        // ждем, пока будет понятно выполнен/нет и (в идеале) дата исполнения
        if (!isPerfLoaded) return

        const start = parseYmdUtc(contractStartDate)
        const end = parseYmdUtc(contractEndDate)
        const today = todayUtc()

        const termDays = diffDays(start, end)

        // просрочка для НЕ исполненного: от конца до сегодня
        const overdueDaysNotCompleted = isOverdue ? diffDays(end, today) : 0

        // просрочка для ИСПОЛНЕННОГО: от конца до completion_stage_end_date
        const overdueDaysCompleted = (() => {
            if (!completionStageEndDate || !isYmd(completionStageEndDate))
                return 0
            const completionEnd = parseYmdUtc(completionStageEndDate)
            const d = diffDays(end, completionEnd)
            return d > 0 ? d : 0
        })()

        const overdueDays = isCompleted
            ? overdueDaysCompleted
            : overdueDaysNotCompleted

        let mappedData: PieChartData[] = []

        // 3) не исполнен + просрочен -> только срок и просрочка
        // 4) исполнен -> только срок и просрочка (0 или до completion_stage_end_date)
        if ((isOverdue && !isCompleted) || isCompleted) {
            mappedData = [
                {
                    label: 'Срок исполнения договора',
                    value: termDays,
                    isTotal: true,
                },
                {
                    label: 'Просрочка по сроку',
                    value: overdueDays,
                    isTotal: true,
                },
            ]
        } else {
            // активный, не просрочен, не исполнен -> 4 критерия
            mappedData = [
                {
                    label: 'Срок исполнения договора',
                    value: termDays,
                    isTotal: true,
                },
                {
                    label: 'Прошло времени',
                    value: start > today ? 0 : diffDays(start, today),
                },
                {
                    label: 'Осталось времени',
                    value: diffDays(today, end),
                },
                { label: 'Просрочка по сроку', value: 0, isTotal: true },
            ]
        }

        setPieData(mappedData)

        // если исполнен и просрочки нет -> 0, иначе -> 1
        const desiredIndex = isCompleted && overdueDays === 0 ? 0 : 1

        let safeIndex = desiredIndex

        // защита по границам
        if (safeIndex < 0 || safeIndex >= mappedData.length) safeIndex = 0

        // доп. защита: если вдруг попали на "Просрочка по сроку=0" (не должно быть, но на всякий)
        if (
            mappedData[safeIndex]?.label === 'Просрочка по сроку' &&
            mappedData[safeIndex]?.value === 0
        ) {
            safeIndex = 0
        }

        setSelectedLabel(mappedData[safeIndex].label)

        setTimeout(() => setIsAnimated(true), 100)
        setTimeout(() => setIsFirstAnimationDone(true), 1100)
        setIsLoading(false)
    }, [
        initialIndex,
        contractStartDate,
        contractEndDate,
        isPerfLoaded,
        isCompleted,
        completionStageEndDate,
        isOverdue,
    ])

    // ---- pie math: only non-total items make segments ----
    const segmentItems = useMemo(
        () => pieData.filter((d) => !d.isTotal),
        [pieData]
    )

    const total = useMemo(
        () => segmentItems.reduce((sum, d) => sum + d.value, 0),
        [segmentItems]
    )

    const selectedItem = useMemo(
        () => pieData.find((d) => d.label === selectedLabel),
        [pieData, selectedLabel]
    )

    const isTotalSelected = !!selectedItem?.isTotal

    const showOverdueBang =
        selectedItem?.label === 'Просрочка по сроку' &&
        (selectedItem?.value ?? 0) > 0

    // Цвет элемента (и для легенды, и для total-круга)
    // const getItemColor = useCallback(
    //     (item: PieChartData): string => {
    //         // total-элементы красим стабильно:
    //         if (item.label === 'Срок исполнения договора') return colors[0]
    //         if (item.label === 'Просрочка по сроку') return colors[3]

    //         // сегментные — по порядку среди НЕ-total
    //         const idx = segmentItems.findIndex((d) => d.label === item.label)
    //         return colors[(idx >= 0 ? idx : 0) % colors.length]
    //     },
    //     [colors, segmentItems]
    // )

    const getItemColor = useCallback(
        (item: PieChartData): string => {
            const idx = LABEL_COLOR_INDEX[item.label]
            if (typeof idx === 'number') return colors[idx % colors.length]
            return colors[0] // fallback
        },
        [colors]
    )

    const radius = 15.9
    const circumference = 2 * Math.PI * radius

    const handlePieDataSelection = useCallback(
        (label: string) => {
            const item = pieData.find((d) => d.label === label)

            // Не даем выбирать "Просрочка по сроку", если она 0
            if (item?.label === 'Просрочка по сроку' && item.value === 0) return

            if (label !== selectedLabel) {
                setSelectedLabel(label)
            }
        },
        [selectedLabel, pieData]
    )

    let offset = 0
    let delay = 0

    const segments =
        total > 0
            ? segmentItems.map((data, index) => {
                  const percentage = (data.value / total) * 100
                  const segmentLength = (percentage / 100) * circumference
                  const isSelected = data.label === selectedLabel
                  const color = getItemColor(data)

                  const segment = (
                      <React.Fragment key={data.label}>
                          <circle
                              cx="18"
                              cy="18"
                              r={radius}
                              fill="transparent"
                              stroke={color}
                              strokeWidth={isSelected ? 4 : 2}
                              strokeDasharray={`${isAnimated ? segmentLength : 0} ${circumference}`}
                              strokeDashoffset={-offset}
                              transform="rotate(0 18 18)"
                              style={{
                                  transition: `stroke-dasharray ${Math.round(percentage) / 100}s linear ${delay}s`,
                              }}
                          />
                          {isFirstAnimationDone && (
                              <text
                                  x="18"
                                  y="20"
                                  textAnchor="middle"
                                  fontSize="4"
                                  fill={
                                      isSelected
                                          ? theme == 'dark'
                                              ? '#FFFFFF'
                                              : '#181818'
                                          : 'transparent'
                                  }
                                  transform="rotate(90 18 18)"
                              >
                                  {Math.round(percentage * 100) / 100} %
                              </text>
                          )}
                      </React.Fragment>
                  )

                  delay += Math.round(percentage) / 100
                  offset += segmentLength
                  return segment
              })
            : []

    return (
        <div className="flex flex-col w-full h-full space-y-3">
            <div className="flex flex-row text-[#181818] dark:text-[#FFFFFF] text-[16px]">
                <div className="flex flex-1">
                    Срок исполнения договора (дни)
                </div>
                <div style={{ cursor: 'pointer' }}>
                    <HoverTooltip
                        tooltipContent={
                            <div className="flex flex-col w-[300px] p-2 gap-2 text-justify">
                                <span>
                                    Показатели отражают сроки исполнения
                                    договора в календарных днях на основании дат
                                    начала и окончания исполнения, указанных при
                                    создании договора.
                                </span>
                                <span className="font-bold">
                                    Расчет выполняется автоматически и
                                    обновляется в режиме реального времени.
                                </span>
                                <span>
                                    <span className="font-bold">
                                        «Срок исполнения»
                                    </span>{' '}
                                    - общий срок исполнения, рассчитанный между
                                    датой начала и датой окончания исполнения
                                    договора
                                </span>
                                <span>
                                    <span className="font-bold">
                                        «Прошло времени»
                                    </span>{' '}
                                    - количество календарных дней, прошедших с
                                    даты начала исполнения договора до текущей
                                    даты
                                </span>
                                <span>
                                    <span className="font-bold">
                                        «Осталось времени»
                                    </span>{' '}
                                    - количество календарных дней, оставшихся от
                                    текущей даты до даты окончания исполнения
                                    договора
                                </span>
                                <span>
                                    <span className="font-bold">
                                        «Просрочка по сроку»
                                    </span>{' '}
                                    - количество календарных дней просрочки
                                    после даты окончания исполнения договора.
                                    При наличии просрочки показатели «Прошло
                                    времени» и «Осталось времени» не
                                    отображаются: в режиме просрочки выводятся
                                    только «Срок исполнения» и «Просрочка по
                                    сроку»
                                </span>
                            </div>
                        }
                        align="left"
                    >
                        <svg
                            className="w-5 h-5"
                            id="info-circle"
                            data-name="Layer 1"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                        >
                            <path
                                fill={theme == 'dark' ? '#FFFFFF' : '#181818'}
                                d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1.25,1.25,0,0,0,12,7.5Z"
                            ></path>
                        </svg>
                    </HoverTooltip>
                </div>
            </div>
            {!contractStartDate && !contractEndDate ? (
                <div className="flex text-[#181818] dark:text-[#FFFFFF] text-[16px] w-full h-full items-center justify-center">
                    Нет данных
                </div>
            ) : (
                <>
                    {isLoading ? (
                        <div className="flex flex-row text-[#181818] dark:text-[#FFFFFF] text-[16px] w-full h-full items-center justify-center gap-2">
                            <svg
                                className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                ></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                ></path>
                            </svg>
                            <span>Загрузка данных</span>
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-row h-full space-x-5 justify-start">
                                <div className="flex items-center">
                                    <svg
                                        viewBox="0 0 36 36"
                                        className="pie-chart"
                                        aria-label="Диаграмма закупок"
                                        aria-live="polite"
                                    >
                                        {segments}
                                        {isTotalSelected && (
                                            <React.Fragment key="total-ring">
                                                <circle
                                                    r="15.9"
                                                    cx="18"
                                                    cy="18"
                                                    fill="transparent"
                                                    stroke={
                                                        selectedItem
                                                            ? getItemColor(
                                                                  selectedItem
                                                              )
                                                            : colors[0]
                                                    }
                                                    strokeWidth={4}
                                                    strokeDasharray={`100 0`}
                                                    strokeDashoffset={0}
                                                    style={{
                                                        transition:
                                                            'stroke-dasharray 0.5s ease, stroke 0.5s ease',
                                                    }}
                                                />
                                                {isFirstAnimationDone && (
                                                    <text
                                                        x="18"
                                                        y="20"
                                                        textAnchor="middle"
                                                        fontSize="4"
                                                        fill={
                                                            theme == 'dark'
                                                                ? '#FFFFFF'
                                                                : '#181818'
                                                        }
                                                        transform="rotate(90 18 18)"
                                                    >
                                                        {showOverdueBang
                                                            ? '!'
                                                            : '100 %'}
                                                    </text>
                                                )}
                                            </React.Fragment>
                                        )}
                                    </svg>
                                </div>
                                <div className="flex items-center">
                                    <ul className="flex flex-col items-start space-y-1">
                                        {pieData.map((item, index) => (
                                            <li
                                                key={item.label}
                                                style={{
                                                    color:
                                                        selectedLabel ===
                                                        item.label
                                                            ? theme == 'dark'
                                                                ? '#FFFFFF'
                                                                : '#181818'
                                                            : theme == 'dark'
                                                              ? '#8A8A8A'
                                                              : '#6A6A6A',
                                                }}
                                                className="flex flex-row items-center space-x-2 text-[12.8px]"
                                            >
                                                <svg width="24" height="24">
                                                    {selectedLabel ===
                                                        item.label && (
                                                        <circle
                                                            cx="12"
                                                            cy="12"
                                                            r="8"
                                                            fill="none"
                                                            stroke={
                                                                getItemColor(
                                                                    item
                                                                ) + '55'
                                                            }
                                                            strokeWidth="5"
                                                        />
                                                    )}
                                                    <circle
                                                        cx="12"
                                                        cy="12"
                                                        r="6"
                                                        fill={getItemColor(
                                                            item
                                                        )}
                                                    />
                                                </svg>
                                                <button
                                                    aria-label={`Выбрать ${item.label}`}
                                                    onClick={() =>
                                                        handlePieDataSelection(
                                                            item.label
                                                        )
                                                    }
                                                >
                                                    <div className="text-left">
                                                        {item.label}
                                                    </div>
                                                    <div className="text-left">
                                                        {item.value}
                                                    </div>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    )
}

export default PieChartPerformanceDate
